import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Loader2, 
  MapPin, 
  Calendar, 
  DollarSign,
  User,
  Star,
  CheckCircle,
  MessageSquare,
  Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JobDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [submittingBid, setSubmittingBid] = useState(false);
  const [bidData, setBidData] = useState({
    amount: '',
    message: '',
    estimated_days: ''
  });

  useEffect(() => {
    fetchJobDetails();
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      const jobRes = await axios.get(`${API}/jobs/${id}`);
      setJob(jobRes.data);

      if (user) {
        try {
          const bidsRes = await axios.get(`${API}/bids/job/${id}`);
          setBids(bidsRes.data);
        } catch (e) {
          // User might not have access to bids
        }
      }
    } catch (error) {
      toast.error('Failed to load job details');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleBidSubmit = async (e) => {
    e.preventDefault();
    setSubmittingBid(true);

    try {
      await axios.post(`${API}/bids`, {
        job_id: id,
        amount: parseFloat(bidData.amount),
        message: bidData.message,
        estimated_days: bidData.estimated_days ? parseInt(bidData.estimated_days) : null
      });
      toast.success('Bid submitted successfully!');
      setBidDialogOpen(false);
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bid');
    } finally {
      setSubmittingBid(false);
    }
  };

  const handleAcceptBid = async (bidId) => {
    try {
      await axios.put(`${API}/bids/${bidId}/accept`);
      toast.success('Bid accepted! The contractor has been notified.');
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept bid');
    }
  };

  const handlePayment = async (bidId) => {
    try {
      const response = await axios.post(`${API}/payments/job/checkout`, {
        bid_id: bidId,
        job_id: id,
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-900/50 text-green-400 border-green-800',
      in_progress: 'bg-yellow-900/50 text-yellow-400 border-yellow-800',
      completed: 'bg-blue-900/50 text-blue-400 border-blue-800',
      cancelled: 'bg-red-900/50 text-red-400 border-red-800'
    };
    return styles[status] || styles.open;
  };

  const getBidStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-900/50 text-yellow-400 border-yellow-800',
      accepted: 'bg-green-900/50 text-green-400 border-green-800',
      rejected: 'bg-red-900/50 text-red-400 border-red-800'
    };
    return styles[status] || styles.pending;
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    return user.role === 'contractor' ? '/contractor/dashboard' : '/customer/dashboard';
  };

  const userAlreadyBid = bids.some(b => b.contractor_id === user?.id);
  const isOwner = job?.customer_id === user?.id;

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-stone-950" data-testid="job-detail-page">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to={getDashboardLink()} 
          className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="industrial-card p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <Badge className="bg-orange-900/50 text-orange-400 border-orange-800 rounded-sm uppercase text-xs">
                  {job.category.replace('_', ' ')}
                </Badge>
                <Badge className={`${getStatusBadge(job.status)} rounded-sm uppercase text-xs`}>
                  {job.status.replace('_', ' ')}
                </Badge>
              </div>

              <h1 className="font-heading text-3xl text-white uppercase">{job.title}</h1>
              
              <div className="flex flex-wrap items-center gap-4 mt-4 text-stone-400">
                <span className="flex items-center gap-2">
                  <User size={16} />
                  {job.customer_name}
                </span>
                <span className="flex items-center gap-2">
                  <MapPin size={16} />
                  {job.location}
                </span>
                <span>{job.acreage} acres</span>
                {job.deadline && (
                  <span className="flex items-center gap-2">
                    <Calendar size={16} />
                    Due: {new Date(job.deadline).toLocaleDateString()}
                  </span>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-stone-700">
                <h2 className="font-heading text-xl text-orange-500 uppercase mb-3">Description</h2>
                <p className="text-stone-300 whitespace-pre-wrap">{job.description}</p>
              </div>

              {job.requirements && job.requirements.length > 0 && (
                <div className="mt-6 pt-6 border-t border-stone-700">
                  <h2 className="font-heading text-xl text-orange-500 uppercase mb-3">Requirements</h2>
                  <ul className="space-y-2">
                    {job.requirements.map((req, index) => (
                      <li key={index} className="flex items-start gap-2 text-stone-300">
                        <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Bids Section */}
            {(isOwner || user?.role === 'contractor') && (
              <div className="industrial-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl text-white uppercase">
                    Bids ({bids.length})
                  </h2>
                </div>

                {bids.length === 0 ? (
                  <p className="text-stone-400 text-center py-8">No bids yet</p>
                ) : (
                  <div className="space-y-4">
                    {bids.map((bid) => (
                      <div 
                        key={bid.id} 
                        className="bg-stone-900 border border-stone-700 rounded-sm p-4"
                        data-testid={`bid-${bid.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <span className="font-heading text-lg text-white">{bid.contractor_name}</span>
                              {bid.company_name && (
                                <span className="text-stone-400">• {bid.company_name}</span>
                              )}
                              <Badge className={`${getBidStatusBadge(bid.status)} rounded-sm uppercase text-xs`}>
                                {bid.status}
                              </Badge>
                            </div>
                            <p className="text-stone-400 mt-2">{bid.message}</p>
                            {bid.estimated_days && (
                              <p className="text-stone-500 text-sm mt-1">
                                Est. completion: {bid.estimated_days} days
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="font-heading text-2xl text-orange-500">
                              ${bid.amount.toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {isOwner && job.status === 'open' && bid.status === 'pending' && (
                          <div className="flex gap-2 mt-4 pt-4 border-t border-stone-700">
                            <Button 
                              onClick={() => handleAcceptBid(bid.id)}
                              data-testid={`accept-bid-${bid.id}`}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              Accept Bid
                            </Button>
                            <Link to={`/messages?user=${bid.contractor_id}`}>
                              <Button variant="outline" className="border-stone-700">
                                <MessageSquare size={16} className="mr-2" />
                                Message
                              </Button>
                            </Link>
                          </div>
                        )}

                        {isOwner && bid.status === 'accepted' && job.payment_status !== 'paid' && (
                          <div className="mt-4 pt-4 border-t border-stone-700">
                            <Button 
                              onClick={() => handlePayment(bid.id)}
                              data-testid={`pay-bid-${bid.id}`}
                              className="bg-orange-500 hover:bg-orange-600 text-white"
                            >
                              <DollarSign size={16} className="mr-2" />
                              Pay ${bid.amount.toLocaleString()}
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Budget Card */}
            {(job.budget_min || job.budget_max) && (
              <div className="industrial-card p-6">
                <h3 className="font-heading text-lg text-stone-400 uppercase mb-2">Budget Range</h3>
                <div className="font-heading text-3xl text-orange-500">
                  ${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}
                </div>
              </div>
            )}

            {/* Bid CTA */}
            {user?.role === 'contractor' && job.status === 'open' && !userAlreadyBid && (
              <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="submit-bid-btn"
                    className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide text-lg rounded-sm"
                  >
                    Submit Bid
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-stone-900 border-stone-700">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-white uppercase">Submit Your Bid</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleBidSubmit} className="space-y-4 mt-4">
                    <div>
                      <label className="form-label">Bid Amount ($)</label>
                      <Input
                        type="number"
                        value={bidData.amount}
                        onChange={(e) => setBidData({ ...bidData, amount: e.target.value })}
                        placeholder="Your price for this job"
                        className="mt-2 bg-stone-800 border-stone-700 h-12"
                        data-testid="bid-amount"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Estimated Days to Complete</label>
                      <Input
                        type="number"
                        value={bidData.estimated_days}
                        onChange={(e) => setBidData({ ...bidData, estimated_days: e.target.value })}
                        placeholder="e.g., 5"
                        className="mt-2 bg-stone-800 border-stone-700 h-12"
                        data-testid="bid-days"
                      />
                    </div>
                    <div>
                      <label className="form-label">Message to Customer</label>
                      <Textarea
                        value={bidData.message}
                        onChange={(e) => setBidData({ ...bidData, message: e.target.value })}
                        placeholder="Describe your qualifications, equipment, and approach..."
                        className="mt-2 bg-stone-800 border-stone-700 min-h-32"
                        data-testid="bid-message"
                        required
                      />
                    </div>
                    <Button 
                      type="submit"
                      disabled={submittingBid}
                      data-testid="confirm-bid-btn"
                      className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase"
                    >
                      {submittingBid ? <Loader2 className="animate-spin" /> : 'Submit Bid'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {userAlreadyBid && (
              <div className="industrial-card p-6 text-center">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-stone-300">You've submitted a bid on this job</p>
              </div>
            )}

            {!user && (
              <Link to="/auth" className="block">
                <Button className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide text-lg rounded-sm">
                  Sign In to Bid
                </Button>
              </Link>
            )}

            {/* Contact Card */}
            {user && !isOwner && (
              <div className="industrial-card p-6">
                <h3 className="font-heading text-lg text-white uppercase mb-4">Contact Customer</h3>
                <Link to={`/messages?user=${job.customer_id}&job=${job.id}`}>
                  <Button className="w-full bg-stone-800 hover:bg-stone-700 text-white border border-stone-700">
                    <MessageSquare size={16} className="mr-2" />
                    Send Message
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
